import { BindingEngine, computedFrom, Disposable } from "aurelia-binding";
import { EventAggregator } from "aurelia-event-aggregator";
import { I18N } from "aurelia-i18n";
import { Router, RouterEvent } from "aurelia-router";
import { RightShipQiRouterConstants } from "../../../../../configuration/rightshipqi-router.constants";
import { Claims } from "../../../../../constants/authentication/claims";
import { UrlHelper } from "../../../../../helpers/url-helper";
import { StepModel } from "../../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { BaseValidationResponse } from "../../../../../models/validations/validations.models";
import { VesselDetailModel } from "../../../../../models/vessels/vessel.models";
import { AutoScreenStepEnum, VetClientModel } from "../../../../../models/vetting/vetting.models";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { NotificationClientService } from "../../../../../services/basics/notification-client.service";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { ValidationsClientService } from "../../../../../services/validations/validations-client.service";
import { ManageVesselService } from "../../../../../services/vessels/manage-vessel.service";
import { Reflection } from "../../../../../utilities/reflection";
import { RatingBaseEntity } from "../../common-steps/base-rating-entity";
import { VetRulesRun } from "../../vetting/steps/rules-run/vet-rules-run";
import { VetCriteria } from "../../vetting/steps/vet-criteria/vet-criteria";


type VetSource = 'vessels' | 'activities';
export abstract class BaseAutoScreeningTemplate extends RatingBaseEntity<VetClientModel> {

    vessel:VesselDetailModel;
    shouldRemoveCacheOnDetached: boolean = true;
    canViewVettingEmailHistory: boolean;

    constructor(router:Router, bindingEngine:BindingEngine, i18N:I18N, notificationService:NotificationClientService, 
        eventAggregator:EventAggregator, validationsClientService:ValidationsClientService,
        signalClientService: SignalClientService, manageVesselService:ManageVesselService,
        private vetCriteria: VetCriteria, private vetRulesRun: VetRulesRun,
        protected userClaimsService: UserClaimsService){
        super(router, bindingEngine, i18N, notificationService, eventAggregator, 
            validationsClientService, signalClientService, manageVesselService);
    }

    async activate(param :{ item:VetClientModel, vessel:VesselDetailModel }){
        this.item = param.item;
        this.onItemSet();
        this.vessel = param.vessel;
        this.refreshSteps();
    }

    protected onItemSet()
    {

    }

    subscription: Disposable;
    attached(){
        super.attached();
        this.subscription = this.eventAggregator.subscribe(RouterEvent.Processing, (x) => {
            if (x && x.instruction && x.instruction.config) {
                const name = x.instruction.config.name;
                this.shouldRemoveCacheOnDetached = name != RightShipQiRouterConstants.RouteNames.PSC && name != RightShipQiRouterConstants.RouteNames.Incident;
            }
        });
    }

    detached(){
        super.detached();
        if(this.subscription) this.subscription.dispose();
    }

    navigateBack() {
        this.router.navigateBack();
    }
    
    @computedFrom(Reflection.getPropertyPath<BaseAutoScreeningTemplate>(v => v.router.currentInstruction))
    public get previousInstructionTitle(): string {
        const source: VetSource = UrlHelper.getParameterValueByKey("source") as VetSource;

        switch (source) {
            case 'vessels':
                return `${this.i18N.tr('vessel-text')}`;
            case 'activities':
                return `${this.i18N.tr('tasks-text')}`;
            default:
                {
                    const prevInstruction = this.router.currentInstruction.previousInstruction;
                    return prevInstruction
                        ? prevInstruction.config.title
                        : this.i18N.tr('back-text');
                }
        }
    }
    
    protected getListRoute(): string {
        return RightShipQiRouterConstants.RouteNames.Activities;
    }

    protected async getItem(): Promise<VetClientModel> {
        return this.item;
    }
   
    protected getItemId(): number {
        return this.item.vetId;
    }
   
    protected saveItem(): Promise<BaseValidationResponse> {
        return null;
    }
   
    protected getUpdateRoute(): string {
        return RightShipQiRouterConstants.RouteNames.Vet;
    }
   
    protected getEntityType(): EntityTypes {
        return EntityTypes.Vet;
    }
   
    protected initializeItemForCreate(): VetClientModel | Promise<VetClientModel> {
        return null;
    }

    protected getCriteriaTitle(){
        return this.i18N.tr("auto-screening-criteria-text");
    }

    protected getScreeningCommonSteps(): StepModel[]
    {
        return [
            {
                lazyLoad: true,
                title: this.getCriteriaTitle(),
                step: AutoScreenStepEnum.VetCriteria,
                vm: this.vetCriteria,
                show: this.userClaimsService.hasPermission(Claims.CanViewVettingCriteria),
                onClick: () => {
                    setTimeout(() => {
                        this.vetCriteria.startCoachMark();
                    });
                },
                getModel: () => {
                    return { item: this.item }
                }
            },
            {
                lazyLoad: true,
                title: this.i18N.tr("vet-rules-run-text"),
                step: AutoScreenStepEnum.VetRulesRun,
                vm: this.vetRulesRun,
                show: this.userClaimsService.hasPermission(Claims.CanViewScreeningRunsTab)
            }
        ];
    }
}